import React, { useMemo } from 'react';
import { Empty, Typography } from 'antd';
import { 
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, 
  CartesianGrid, Tooltip, Legend 
} from 'recharts';
import dayjs from 'dayjs';

const { Text } = Typography;

const colors = ['#1890ff', '#52c41a', '#faad14', '#f5222d', '#722ed1', '#13c2c2', '#eb2f96', '#fa8c16'];

const TrendChart = ({ 
  data, 
  height = 300, 
  comparisonMode = false, 
  comparisonPeriod = 'previous_month',
  dateRange,
  comparisonData = null 
}) => {
  // Generate comparison data when in comparison mode
  const processedData = useMemo(() => {
    if (!data || data.length === 0) {
      return [];
    }

    if (!comparisonMode || !comparisonData) {
      return data;
    }

    // Merge current data with comparison data
    const mergedData = [...data];
    
    // Add comparison data points with _comparison suffix
    if (comparisonData.data && comparisonData.data.length > 0) {
      comparisonData.data.forEach((compPoint, index) => {
        if (mergedData[index]) {
          // Add comparison values to existing data points
          Object.keys(compPoint).forEach(key => {
            if (key !== 'date' && typeof compPoint[key] === 'number') {
              mergedData[index][`${key}_comparison`] = compPoint[key];
            }
          });
          // Add comparison date for tooltip
          mergedData[index]['comparison_date'] = compPoint.date;
        }
      });
    }
    
    return mergedData;
  }, [data, comparisonMode, comparisonData]);

  if (!data || data.length === 0) {
    return <Empty description="Veri bulunamadı" />;
  }

  // Update metrics to include comparison metrics when needed
  const allMetrics = Object.keys(processedData[0] || {}).filter(key => key !== 'date');
  const currentMetrics = allMetrics.filter(key => !key.includes('_comparison'));
  const comparisonMetrics = allMetrics.filter(key => key.includes('_comparison'));

  // Revenue'yu öncelikli olarak göster
  const prioritizedCurrentMetrics = [];
  
  // Önce REVENUE'yu bul ve ekle
  const revenueMetric = currentMetrics.find(metric => metric === 'REVENUE');
  if (revenueMetric) {
    prioritizedCurrentMetrics.push(revenueMetric);
  }
  
  // Sonra diğer önemli metrikleri ekle
  const importantMetrics = ['ROI', 'COST(ALL)', 'COS'];
  importantMetrics.forEach(important => {
    const found = currentMetrics.find(metric => metric === important);
    if (found && !prioritizedCurrentMetrics.includes(found)) {
      prioritizedCurrentMetrics.push(found);
    }
  });
  
  // En fazla 3 metrik göster (mevcut + karşılaştırma)
  const baseMetrics = prioritizedCurrentMetrics.slice(0, comparisonMode ? 2 : 3);
  let metricsToShow = [...baseMetrics];
  
  // Comparison mode'da karşılaştırma metriklerini ekle
  if (comparisonMode) {
    baseMetrics.forEach(metric => {
      const comparisonMetric = `${metric}_comparison`;
      if (comparisonMetrics.includes(comparisonMetric)) {
        metricsToShow.push(comparisonMetric);
      }
    });
  }

  const periodLabels = {
    'previous_week': 'Önceki Hafta',
    'previous_month': 'Önceki Ay', 
    'previous_quarter': 'Önceki Çeyrek',
    'previous_year': 'Önceki Yıl',
    'same_period_last_year': 'Geçen Yılın Aynı Dönemi',
    'custom': 'Özel Tarih Aralığı'
  };
  

  return (
    <div>
      {comparisonMode && (
        <div style={{ 
          marginBottom: 12, 
          padding: '8px 12px', 
          backgroundColor: comparisonData ? '#f6ffed' : '#fff7e6', 
          border: `1px solid ${comparisonData ? '#b7eb8f' : '#ffd591'}`,
          borderRadius: '6px'
        }}>
          <Text style={{ fontSize: '12px', color: comparisonData ? '#389e0d' : '#d48806' }}>
            {comparisonData 
              ? `📊 Karşılaştırma: ${comparisonData.startDate} - ${comparisonData.endDate}`
              : '⏳ Karşılaştırma verileri yükleniyor...'
            }
          </Text>
        </div>
      )}
      <ResponsiveContainer width="100%" height={height}>
        <AreaChart data={processedData}>
          <defs>
            {metricsToShow.map((metric, index) => (
              <linearGradient key={metric} id={`gradient${index}`} x1="0" y1="0" x2="0" y2="1">
                <stop 
                  offset="5%" 
                  stopColor={colors[index % colors.length]} 
                  stopOpacity={metric.includes('_comparison') ? 0.5 : 0.8}
                />
                <stop 
                  offset="95%" 
                  stopColor={colors[index % colors.length]} 
                  stopOpacity={metric.includes('_comparison') ? 0.05 : 0.1}
                />
              </linearGradient>
            ))}
          </defs>
          <XAxis dataKey="date" />
          <YAxis />
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <Tooltip 
            formatter={(value, name, props) => {
              let displayName = name;
              
              // Handle comparison metric names with specific date
              if (name.includes('_comparison')) {
                const baseMetric = name.replace('_comparison', '');
                const comparisonDate = props.payload.comparison_date;
                displayName = `${baseMetric} (${comparisonDate})`;
              }
              
              if (name === 'REVENUE' || name.includes('COST') || name.includes('REVENUE_comparison') || name.includes('COST')) {
                return [new Intl.NumberFormat('tr-TR', {
                  style: 'currency',
                  currency: 'TRY',
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0
                }).format(value), displayName];
              }
              return [value, displayName];
            }}
          />
          <Legend />
          {metricsToShow.map((metric, index) => (
            <Area
              key={metric}
              type="monotone"
              dataKey={metric}
              stroke={colors[index % colors.length]}
              fill={`url(#gradient${index})`}
              strokeWidth={metric.includes('_comparison') ? 1 : 2}
              strokeDasharray={metric.includes('_comparison') ? '5,5' : 'none'}
              fillOpacity={metric.includes('_comparison') ? 0.3 : 0.6}
            />
          ))}
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

export default React.memo(TrendChart);